import { FocusMonitor, FocusOrigin, isFakeMousedownFromScreenReader } from '@angular/cdk/a11y';
import { Direction, Directionality } from '@angular/cdk/bidi';
import {
    FlexibleConnectedPositionStrategy,
    HorizontalConnectionPos,
    Overlay,
    OverlayConfig,
    OverlayRef,
    ScrollStrategy,
    VerticalConnectionPos,
} from '@angular/cdk/overlay';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    InjectionToken,
    Input,
    OnDestroy,
    Optional,
    Output,
    ViewContainerRef,
} from '@angular/core';
import { merge, Subscription } from 'rxjs';
import { MenuPositionX, MenuPositionY } from './menu-panel';
import { MenuCloseReason, MenuComponent } from './menu.component';

/** Injection token that determines the scroll handling while the menu is open. */
export const ICC_MENU_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>(
    'icc-menu-scroll-strategy'
);

/** @docs-private */
export function ICC_MENU_SCROLL_STRATEGY_FACTORY(overlay: Overlay): () => ScrollStrategy {
    return () => overlay.scrollStrategies.close();
}

/** @docs-private */
export const ICC_MENU_SCROLL_STRATEGY_FACTORY_PROVIDER = {
    provide: ICC_MENU_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: ICC_MENU_SCROLL_STRATEGY_FACTORY,
};

@Directive({
    selector: '[iccMenuTriggerFor]',
    exportAs: 'iccMenuTrigger',
})
export class MenuTriggerDirective implements OnDestroy {
    private _portal?: TemplatePortal;
    private _overlayRef: OverlayRef | null = null;
    private _menuOpen = false;
    private _scrollStrategy: () => ScrollStrategy;

    /** Tracks input type  */
    private _openedBy: 'mouse' | 'touch' | null = null;

    private _closingActionsSubscription = Subscription.EMPTY;
    private _menuCloseSubscription = Subscription.EMPTY;

    /** Event emitted when the associated menu is opened. */
    @Output() readonly menuOpened: EventEmitter<void> = new EventEmitter<void>();

    /** Event emitted when the associated menu is closed. */
    @Output() readonly menuClosed: EventEmitter<void> = new EventEmitter<void>();

    @HostBinding('attr.aria-haspopup') ariaHasPopup = true;

    @HostBinding('attr.aria-expanded')
    get menuOpen(): boolean {
        return this._menuOpen;
    }

    private _menu!: MenuComponent;
    /** References the menu instance that the trigger is associated with. */
    @Input('iccMenuTriggerFor')
    get menu() {
        return this._menu;
    }
    set menu(menu: MenuComponent) {
        if (menu === this._menu) {
            return;
        }

        this._menu = menu;
        this._menuCloseSubscription.unsubscribe();

        if (menu) {
            this._menuCloseSubscription = menu.closed.asObservable().subscribe((reason) => {
                this._destroyMenu();
            });
        }
    }

    restoreFocus = true;

    // eslint-disable-next-line max-params
    constructor(
        private _overlay: Overlay,
        private _element: ElementRef<HTMLElement>,
        private _viewContainerRef: ViewContainerRef,
        @Inject(ICC_MENU_SCROLL_STRATEGY) scrollStrategy: any,
        private _focusMonitor: FocusMonitor,
        @Optional() private _dir: Directionality
    ) {
        this._scrollStrategy = scrollStrategy;
        // On touch devices set _openedBy to 'touch'
        _element.nativeElement.addEventListener(
            'touchstart',
            () => (this._openedBy = 'touch'),
            normalizePassiveListenerOptions({ passive: true })
        );
    }

    ngOnDestroy() {
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._overlayRef = null;
        }

        this._element.nativeElement.removeEventListener(
            'touchstart',
            () => (this._openedBy = 'touch'),
            normalizePassiveListenerOptions({ passive: true })
        );

        this._menuCloseSubscription.unsubscribe();
        this._closingActionsSubscription.unsubscribe();
    }

    /** Toggles the menu between the open and closed states. */
    toggleMenu(): void {
        return this._menuOpen ? this.closeMenu() : this.openMenu();
    }

    /** Opens the menu. */
    openMenu(): void {
        if (this._menuOpen) {
            return;
        }

        const overlayRef = this._createOverlay();
        const overlayConfig = overlayRef.getConfig();

        this._setPosition(overlayConfig.positionStrategy as FlexibleConnectedPositionStrategy);
        overlayConfig.hasBackdrop = this.menu.hasBackdrop;
        overlayRef.attach(this._getPortal());
        this._closingActionsSubscription = this._menuClosingActions().subscribe(() =>
            this.closeMenu()
        );

        this._initMenu();

        if (this.menu instanceof MenuComponent) {
            this.menu._startAnimation();
        }
    }

    /** Closes the menu. */
    closeMenu(): void {
        this.menu.closed.emit();
    }

    /** The text direction of the containing app. */
    get dir(): Direction {
        return this._dir && this._dir.value === 'rtl' ? 'rtl' : 'ltr';
    }

    /** Focuses the menu trigger. */
    focus(origin: FocusOrigin = 'program'): void {
        this._focusMonitor.focusVia(this._element, origin);
    }

    /** Handles click events on the trigger. */
    @HostListener('click', ['$event'])
    handleClick(event: MouseEvent): void {
        this.toggleMenu();
    }

    /** Handles mouse presses on the trigger. */
    @HostListener('mousedown', ['$event'])
    handleMousedown(event: MouseEvent): void {
        if (!isFakeMousedownFromScreenReader(event)) {
            // Since right or middle button clicks won't trigger the `click` event,
            // we shouldn't consider the menu as opened by mouse in those cases.
            this._openedBy = event.button === 0 ? 'mouse' : null;
        }
    }

    /** Returns a stream that emits whenever an action that should close the menu occurs. */
    private _menuClosingActions() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const backdrop = this._overlayRef!.backdropClick();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const detachments = this._overlayRef!.detachments();

        return merge(backdrop, detachments);
    }

    /**
     * This method creates the overlay from the provided menu's template and saves its
     * OverlayRef so that it can be attached to the DOM when openMenu is called.
     */
    private _createOverlay(): OverlayRef {
        if (!this._overlayRef) {
            const config = this._getOverlayConfig();
            this._subscribeToPositions(
                config.positionStrategy as FlexibleConnectedPositionStrategy
            );
            this._overlayRef = this._overlay.create(config);
            this._overlayRef.keydownEvents().subscribe();
        }

        return this._overlayRef;
    }

    /**
     * This method builds the configuration object needed to create the overlay, the OverlayState.
     * @returns OverlayConfig
     */
    private _getOverlayConfig(): OverlayConfig {
        return new OverlayConfig({
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._element)
                .withLockedPosition()
                .withGrowAfterOpen()
                .withTransformOriginOn('.menu-panel')
                .withViewportMargin(8),
            backdropClass: this.menu.backdropClass || 'cdk-overlay-transparent-backdrop',
            panelClass: this.menu.overlayPanelClass,
            scrollStrategy: this._scrollStrategy(),
            direction: this._dir,
        });
    }

    /**
     * Listens to changes in the position of the overlay and sets the correct classes
     * on the menu based on the new position. This ensures the animation origin is always
     * correct, even if a fallback position is used for the overlay.
     */
    private _subscribeToPositions(position: FlexibleConnectedPositionStrategy): void {
        if (this.menu.setPositionClasses) {
            position.positionChanges.subscribe((change) => {
                const posX: MenuPositionX =
                    change.connectionPair.overlayX === 'start' ? 'after' : 'before';
                const posY: MenuPositionY =
                    change.connectionPair.overlayY === 'top' ? 'below' : 'above';

                this.menu.setPositionClasses?.(posX, posY);
            });
        }
    }

    /** Gets the portal that should be attached to the overlay. */
    private _getPortal(): TemplatePortal {
        if (!this._portal || this._portal.templateRef !== this.menu.templateRef) {
            this._portal = new TemplatePortal(this.menu.templateRef, this._viewContainerRef);
        }

        return this._portal;
    }

    /** Closes the menu and does the necessary cleanup. */
    private _destroyMenu() {
        if (!this._overlayRef || !this._menuOpen) {
            return;
        }

        const menu = this.menu;
        this._closingActionsSubscription.unsubscribe();
        this._overlayRef.detach();
        this.focus(this._openedBy);
        this._openedBy = null;

        if (menu instanceof MenuComponent) {
            menu._resetAnimation();
        }
        this._setIsMenuOpen(false);
    }

    /**
     * This method sets the menu state to open and focuses the first item if
     * the menu was opened via the keyboard.
     */
    private _initMenu(): void {
        this.menu.direction = this.dir;
        this.menu.focusItem(this._openedBy || 'program');
        this._setIsMenuOpen(true);
    }

    // set state rather than toggle to support triggers sharing a menu
    private _setIsMenuOpen(isOpen: boolean): void {
        this._menuOpen = isOpen;
        this._menuOpen ? this.menuOpened.emit() : this.menuClosed.emit();
    }

    /**
     * Sets the appropriate positions on a position strategy
     * so the overlay connects with the trigger correctly.
     * @param positionStrategy Strategy whose position to update.
     */
    private _setPosition(positionStrategy: FlexibleConnectedPositionStrategy) {
        const [originX, originFallbackX]: HorizontalConnectionPos[] =
            this.menu.xPosition === 'before' ? ['end', 'start'] : ['start', 'end'];

        const [overlayY, overlayFallbackY]: VerticalConnectionPos[] =
            this.menu.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

        let [originY, originFallbackY] = [overlayY, overlayFallbackY];
        const [overlayX, overlayFallbackX] = [originX, originFallbackX];
        const offsetY = this.menu.yPosition === 'above' ? -4 : 4;

        if (!this.menu.overlapTrigger) {
            originY = overlayY === 'top' ? 'bottom' : 'top';
            originFallbackY = overlayFallbackY === 'top' ? 'bottom' : 'top';
        }

        positionStrategy.withPositions([
            { originX, originY, overlayX, overlayY, offsetY },
            { originX: originFallbackX, originY, overlayX: overlayFallbackX, overlayY, offsetY },
            {
                originX,
                originY: originFallbackY,
                overlayX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY,
            },
            {
                originX: originFallbackX,
                originY: originFallbackY,
                overlayX: overlayFallbackX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY,
            },
        ]);
    }

}
